import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Cpu,
  Database,
  Zap,
  Search,
  MapPin,
  Users,
  History,
  Compass,
  DollarSign,
  Phone,
  BarChart3,
  Map,
  Building2,
  Code2,
  TrendingUp,
  ArrowRight,
  Coins,
  ChevronRight,
  CheckCircle,
} from "lucide-react";
import { SiOpenai, SiAnthropic, SiLangchain, SiReplit, SiZapier } from "react-icons/si";
import { Navigation } from "@/components/navigation";
import { usePageMeta } from "@/hooks/use-page-meta";
import { usePrelaunch } from "@/hooks/use-prelaunch";
import { useEffect, useState, useRef, useCallback } from "react";
import { AnimateOnScroll } from "@/components/animate-on-scroll";
import { CountUp } from "@/components/count-up";

const features = [
  {
    icon: Cpu,
    title: "AI-native integration",
    description:
      "Connect via MCP and let your agent retrieve property details, owner information, parcel boundaries and more using intuitive tool calls.",
  },
  {
    icon: Database,
    title: "158M+ trusted records",
    description:
      "Same nationwide property records and 200+ data attributes that power PropertyMCP. Refreshed daily for nationwide coverage.",
  },
  {
    icon: Zap,
    title: "Millisecond responses",
    description:
      "Structured JSON responses returned in under 500ms. Normalized, consistent, and ready for your agent to process.",
  },
];

type ToolClassification = "Light" | "Heavy" | "Premium";

interface Tool {
  name: string;
  description: string;
  credits: number;
  classification: ToolClassification;
  icon: typeof Search;
}

const tools: Tool[] = [
  { name: "search_properties", description: "Find properties matching criteria like state, acreage, vacancy, and price.", credits: 1, classification: "Light", icon: Search },
  { name: "get_property_by_address", description: "Retrieve comprehensive data for a single property by street address.", credits: 2, classification: "Heavy", icon: Building2 },
  { name: "get_owner_info", description: "Return owner names, mailing addresses and contact info for a parcel.", credits: 3, classification: "Heavy", icon: Users },
  { name: "get_comps", description: "Generate comparable sales based on similarity and proximity.", credits: 4, classification: "Heavy", icon: BarChart3 },
  { name: "get_parcel_geometry", description: "Provide parcel boundaries in GeoJSON format for mapping.", credits: 1, classification: "Light", icon: Map },
  { name: "estimate_land_value", description: "Estimate land value based on comparable sales and market conditions.", credits: 4, classification: "Heavy", icon: DollarSign },
  { name: "find_vacant_land", description: "Search for vacant parcels meeting size, zoning, or location criteria.", credits: 1, classification: "Light", icon: Compass },
  { name: "find_offmarket_properties", description: "Identify unlisted properties with high equity or absentee owners.", credits: 3, classification: "Heavy", icon: TrendingUp },
  { name: "skip_trace_owner", description: "Find phone numbers, emails and contact details for property owners.", credits: 10, classification: "Premium", icon: Phone },
  { name: "find_adjacent_parcels", description: "Retrieve parcels adjacent to a given parcel for assemblage analysis.", credits: 1, classification: "Light", icon: MapPin },
  { name: "get_property_history", description: "Historical assessments, ownership transfers, permits and improvements.", credits: 3, classification: "Heavy", icon: History },
];

const classificationConfig: Record<ToolClassification, { badgeClass: string }> = {
  Light: { badgeClass: "bg-emerald-500/15 text-emerald-400 border-transparent" },
  Heavy: { badgeClass: "bg-amber-500/15 text-amber-400 border-transparent" },
  Premium: { badgeClass: "bg-red-500/15 text-red-400 border-transparent" },
};

const workflowSteps = [
  {
    icon: Search,
    title: "Search",
    tools: ["search_properties"],
    description: "Searches 158M+ properties for vacant land in TX over 5 acres",
    creditLabel: "1 credit",
    isOutput: false,
  },
  {
    icon: Users,
    title: "Enrich",
    tools: ["get_owner_info", "get_parcel_geometry"],
    description: "For each result, fetches owner contact info and parcel boundary maps",
    creditLabel: "20 credits (4 credits \u00d7 5 parcels)",
    isOutput: false,
  },
  {
    icon: CheckCircle,
    title: "Deliver",
    tools: [],
    description: "Returns 5 parcels with acreage, assessed value, owner mailing address, and parcel boundary GeoJSON",
    creditLabel: "",
    isOutput: true,
  },
];

const testimonials = [
  {
    quote: "We replaced 200 lines of API integration code with 3 MCP tool calls. PropertyMCP just works — our agent finds vacant land, pulls comps, and estimates values without us writing any data plumbing.",
    name: "Marcus Chen",
    title: "CTO, LandScout AI",
    initials: "MC",
    gradient: "from-emerald-500 to-emerald-700",
  },
  {
    quote: "The skip tracing tool alone is worth it. We built a lead gen bot that finds off-market properties and contacts owners — all through natural language. Our agents close deals while we sleep.",
    name: "Sarah Mitchell",
    title: "Founder, PropBot",
    initials: "SM",
    gradient: "from-cyan-500 to-cyan-700",
  },
  {
    quote: "We evaluated 4 property data APIs before choosing PropertyMCP. The MCP integration cut our development time from weeks to hours. The credit pricing is transparent and the docs are excellent.",
    name: "James Okafor",
    title: "Lead Engineer, Vestmap",
    initials: "JO",
    gradient: "from-blue-500 to-blue-700",
  },
  {
    quote: "PropertyMCP powers our entire investor copilot. Clients ask questions in plain English and get property data, comps, and owner info back in seconds. It transformed our platform.",
    name: "Elena Rodriguez",
    title: "VP Product, RealStack",
    initials: "ER",
    gradient: "from-purple-500 to-purple-700",
  },
  {
    quote: "As a solo developer building a land investing bot, the free trial and pay-as-you-go pricing made it easy to start. I was pulling property data within 10 minutes of signing up.",
    name: "David Park",
    title: "Indie Developer",
    initials: "DP",
    gradient: "from-amber-500 to-amber-700",
  },
];

type TerminalLine =
  | { kind: "typed"; text: string }
  | { kind: "instant"; text: string; className: string }
  | { kind: "blank" }
  | { kind: "pause"; ms: number }
  | { kind: "json"; lines: string[] }
  | { kind: "status"; text: string };

const terminalScript: TerminalLine[] = [
  { kind: "typed", text: "mcp connect propertymcp.co/mcp/sse" },
  { kind: "status", text: "\u2713 Connected to PropertyMCP (11 tools available)" },
  { kind: "blank" },
  { kind: "typed", text: "mcp call search_properties --state TX --vacant true --limit 3" },
  { kind: "instant", text: "\u23F3 Searching 158M+ properties...", className: "text-gray-500" },
  { kind: "pause", ms: 800 },
  {
    kind: "json",
    lines: [
      '{',
      '  "results": [',
      '    {',
      '      "parcel_id": "TX-342-88921",',
      '      "address": "Rural Route 4, Marfa, TX",',
      '      "acreage": 12.5,',
      '      "assessed_value": 45000',
      '    }',
      '  ],',
      '  "count": 3,',
      '  "credits_used": 1',
      '}',
    ],
  },
  { kind: "status", text: "\u2713 1 credit used \u00b7 24 remaining \u00b7 247ms" },
];

function colorizeJsonLine(line: string) {
  const parts: { text: string; cls: string }[] = [];
  let rest = line;

  while (rest.length > 0) {
    const keyMatch = rest.match(/^(\s*"[^"]+"\s*)(:)/);
    if (keyMatch) {
      parts.push({ text: keyMatch[1], cls: "text-cyan-400" });
      parts.push({ text: ":", cls: "text-gray-500" });
      rest = rest.slice(keyMatch[0].length);
      continue;
    }

    const strMatch = rest.match(/^(\s*"[^"]*")(,?\s*)/);
    if (strMatch) {
      parts.push({ text: strMatch[1], cls: "text-emerald-300" });
      if (strMatch[2]) parts.push({ text: strMatch[2], cls: "text-gray-500" });
      rest = rest.slice(strMatch[0].length);
      continue;
    }

    const numMatch = rest.match(/^(\s*\d+\.?\d*)(,?\s*)/);
    if (numMatch) {
      parts.push({ text: numMatch[1], cls: "text-amber-300" });
      if (numMatch[2]) parts.push({ text: numMatch[2], cls: "text-gray-500" });
      rest = rest.slice(numMatch[0].length);
      continue;
    }

    const bracketMatch = rest.match(/^[\s{}[\],]+/);
    if (bracketMatch) {
      parts.push({ text: bracketMatch[0], cls: "text-gray-500" });
      rest = rest.slice(bracketMatch[0].length);
      continue;
    }

    parts.push({ text: rest, cls: "text-gray-400" });
    break;
  }

  return parts;
}

function HeroTerminal() {
  const [visibleItems, setVisibleItems] = useState<TerminalLine[]>([]);
  const [typingText, setTypingText] = useState("");
  const [showCursor, setShowCursor] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [done, setDone] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const clear = () => timers.current.forEach(clearTimeout);
    let scriptIdx = 0;
    let delay = 1500;

    const processNext = () => {
      if (scriptIdx >= terminalScript.length) {
        setShowCursor(true);
        setIsTyping(false);
        setDone(true);
        return;
      }

      const item = terminalScript[scriptIdx];
      scriptIdx++;

      if (item.kind === "typed") {
        setIsTyping(true);
        setShowCursor(true);
        let charIdx = 0;
        const typeChar = () => {
          if (charIdx <= item.text.length) {
            setTypingText(item.text.slice(0, charIdx));
            charIdx++;
            timers.current.push(setTimeout(typeChar, 25));
          } else {
            setShowCursor(false);
            setIsTyping(false);
            timers.current.push(setTimeout(() => {
              setVisibleItems((prev) => [...prev, item]);
              setTypingText("");
              timers.current.push(setTimeout(processNext, 100));
            }, 400));
          }
        };
        timers.current.push(setTimeout(typeChar, 0));
      } else if (item.kind === "pause") {
        timers.current.push(setTimeout(processNext, item.ms));
      } else if (item.kind === "blank") {
        setVisibleItems((prev) => [...prev, item]);
        timers.current.push(setTimeout(processNext, 100));
      } else {
        setVisibleItems((prev) => [...prev, item]);
        timers.current.push(setTimeout(processNext, item.kind === "json" ? 300 : 100));
      }
    };

    timers.current.push(setTimeout(processNext, delay));
    return clear;
  }, []);

  return (
    <div
      className="rounded-2xl bg-navy-800 border border-navy-700 shadow-2xl shadow-[0_0_80px_rgba(16,185,129,0.08)] overflow-hidden"
      data-testid="terminal-hero"
    >
      <div className="h-10 bg-navy-800 border-b border-navy-700 flex items-center px-4 relative">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FF5F57" }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FEBC2E" }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#28C840" }} />
        </div>
        <span className="absolute left-1/2 -translate-x-1/2 text-gray-500 text-xs">PropertyMCP</span>
      </div>

      <div className="bg-navy-950 p-6 font-mono text-sm leading-relaxed min-h-[360px]">
        {visibleItems.map((item, i) => {
          if (item.kind === "blank") return <div key={i} className="h-3" />;
          if (item.kind === "typed") {
            return (
              <div key={i}>
                <span className="text-emerald-400">$ </span>
                <span className="text-emerald-400">{item.text}</span>
              </div>
            );
          }
          if (item.kind === "instant") {
            return (
              <div key={i} className={`terminal-response-fade ${item.className}`}>
                {item.text}
              </div>
            );
          }
          if (item.kind === "status") {
            return (
              <div key={i} className="text-emerald-400 terminal-response-fade">
                {item.text}
              </div>
            );
          }
          if (item.kind === "json") {
            return (
              <pre key={i} className="terminal-response-fade whitespace-pre">
                {item.lines.map((line, j) => (
                  <div key={j}>
                    {colorizeJsonLine(line).map((part, k) => (
                      <span key={k} className={part.cls}>{part.text}</span>
                    ))}
                  </div>
                ))}
              </pre>
            );
          }
          return null;
        })}

        {isTyping && (
          <div>
            <span className="text-emerald-400">$ </span>
            <span className="text-emerald-400">{typingText}</span>
            {showCursor && <span className="terminal-cursor text-emerald-400">{"\u2588"}</span>}
          </div>
        )}

        {done && (
          <div>
            <span className="text-emerald-400">$ </span>
            <span className="terminal-cursor text-emerald-400">{"\u2588"}</span>
          </div>
        )}
      </div>
    </div>
  );
}


export default function Landing() {
  const prelaunch = usePrelaunch();
  const ctaHref = prelaunch ? "/waitlist" : "/register";

  usePageMeta({
    title: "PropertyMCP - Property Data for AI Agents | MCP Property Data Tools",
    description: "Access 158M+ US properties via MCP-ready endpoints. 11 credit-gated tools for property search, valuations, owner info, comps, and parcel geometry. Built for AI agents and automation.",
    ogTitle: "PropertyMCP - Property Data for AI Agents",
    ogDescription: "Access 158M+ US properties via MCP-ready endpoints. 11 credit-gated tools for property search, valuations, owner info, comps, and parcel geometry.",
    ogType: "website",
    ogUrl: "https://propertymcp.co/",
    ogImage: "https://propertymcp.co/og-image.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PropertyMCP",
      "applicationCategory": "DeveloperApplication",
      "operatingSystem": "Web",
      "url": "https://propertymcp.co",
      "description": "Property data platform for AI agents. 11 MCP tools providing access to 158M+ US properties with credit-gated billing via Stripe.",
      "offers": [
        ...(prelaunch ? [] : [{ "@type": "Offer", "price": "0", "priceCurrency": "USD", "description": "Free trial — 25 credits for 14 days" }]),
        { "@type": "Offer", "price": "99", "priceCurrency": "USD", "description": "Starter plan - 1,000 credits/month" },
        { "@type": "Offer", "price": "299", "priceCurrency": "USD", "description": "Growth plan - 5,000 credits/month" },
        { "@type": "Offer", "price": "499", "priceCurrency": "USD", "description": "Scale plan - 15,000 credits/month" },
        { "@type": "Offer", "price": "999", "priceCurrency": "USD", "description": "Pro plan - 50,000 credits/month" },
      ],
      "featureList": "Property Search, Address Lookup, Owner Info, Comparable Sales, Parcel Geometry, Vacant Land Search, Adjacent Parcels, Property History, Off-Market Properties, Land Valuation, Skip Trace",
    },
  });

  const grouped: Record<ToolClassification, Tool[]> = { Light: [], Heavy: [], Premium: [] };
  tools.forEach((t) => grouped[t.classification].push(t));

  const [toolFilter, setToolFilter] = useState<"All" | ToolClassification>("All");
  const filteredTools = toolFilter === "All" ? tools : tools.filter((t) => t.classification === toolFilter);
  const [codeTab, setCodeTab] = useState<"Python" | "TypeScript" | "cURL">("Python");

  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const isPausedRef = useRef(false);

  const scrollToTestimonial = useCallback((index: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.children[index] as HTMLElement;
    if (card) {
      el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => {
      const scrollLeft = el.scrollLeft;
      const cardWidth = (el.children[0] as HTMLElement)?.offsetWidth ?? 1;
      const gap = 24;
      const idx = Math.round(scrollLeft / (cardWidth + gap));
      setActiveTestimonial(Math.min(idx, testimonials.length - 1));
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPausedRef.current) return;
      setActiveTestimonial((prev) => {
        const next = (prev + 1) % testimonials.length;
        scrollToTestimonial(next);
        return next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [scrollToTestimonial]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main id="main-content">

      <section
        className="relative min-h-screen flex items-center bg-navy-950 pt-16"
        data-testid="section-hero"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" aria-hidden="true">
          <div className="absolute top-[15%] left-[10%] w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-3xl animate-drift-1" />
          <div className="absolute top-[40%] right-[5%] w-[500px] h-[500px] rounded-full bg-cyan-400/[0.08] blur-3xl animate-drift-2" />
          <div className="absolute bottom-[10%] left-[30%] w-[500px] h-[500px] rounded-full bg-navy-700/[0.15] blur-3xl animate-drift-3" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(rgba(51,65,85,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(51,65,85,0.08) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="lg:grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 text-center lg:text-left mb-12 lg:mb-0">
              <p
                className="text-emerald-500 text-sm font-semibold uppercase tracking-[0.15em] mb-6 hero-animate"
                style={{ animationDelay: "0.04s" }}
                data-testid="text-hero-eyebrow"
              >
                Model Context Protocol
              </p>

              <h1
                className="text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight leading-tight text-white hero-animate"
                style={{ animationDelay: "0.08s" }}
                data-testid="text-hero-headline"
              >
                Property data for{" "}
                <span
                  className="pb-1"
                  style={{
                    background: "linear-gradient(135deg, #10B981, #22D3EE)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    display: "inline",
                  }}
                >
                  AI&nbsp;agents
                </span>
              </h1>

              <p
                className="text-lg lg:text-xl leading-relaxed text-gray-400 max-w-xl mx-auto lg:mx-0 mb-8 hero-animate"
                style={{ animationDelay: "0.12s" }}
                data-testid="text-hero-sub"
              >
                Give your AI and automation tools instant access to 158&nbsp;million+
                U.S. properties, parcel boundaries and ownership records through a
                purpose-built MCP server.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4 items-center justify-center lg:justify-start hero-animate"
                style={{ animationDelay: "0.16s" }}
              >
                <Link href={ctaHref} data-testid="link-hero-cta">
                  <span
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-8 py-4 font-semibold text-lg transition-all duration-200 cursor-pointer hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:scale-[1.02]"
                    data-testid="button-start-free-trial"
                  >
                    {prelaunch ? "Join Waitlist" : "Start Free Trial"}
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </Link>
                <Link href="/docs" data-testid="link-view-docs">
                  <span className="inline-flex items-center justify-center text-white border border-navy-700 hover:border-emerald-500 hover:text-emerald-400 rounded-lg px-8 py-4 text-lg transition-all duration-200 cursor-pointer">
                    View Documentation
                  </span>
                </Link>
              </div>

              <p
                className="text-gray-400 text-sm mt-6 hero-animate"
                style={{ animationDelay: "0.2s" }}
                data-testid="text-hero-trust"
              >
                {prelaunch
                  ? "Launching soon · Join the waitlist for early access"
                  : "No credit card required · 25 free credits · 14-day trial"}
              </p>
            </div>

            <div className="lg:col-span-2" style={{ opacity: 0, animation: "terminalEntrance 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.12s forwards" }}>
              <HeroTerminal />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-navy-800 py-16 px-6" data-testid="section-stats">
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <CountUp target={158} suffix="M+" className="text-white text-4xl lg:text-5xl font-bold" />
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-2" data-testid="label-stat-properties">U.S. Properties</p>
            </div>
            <div className="text-center">
              <CountUp target={200} suffix="+" className="text-white text-4xl lg:text-5xl font-bold" />
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-2" data-testid="label-stat-attributes">Data Attributes</p>
            </div>
            <div className="text-center">
              <CountUp target={11} className="text-white text-4xl lg:text-5xl font-bold" />
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-2" data-testid="label-stat-tools">AI-Ready Tools</p>
            </div>
            <div className="text-center">
              <span className="text-white text-4xl lg:text-5xl font-bold" data-testid="stat-response-time">&lt;500ms</span>
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-2" data-testid="label-stat-response">Avg Response Time</p>
            </div>
          </div>
        </AnimateOnScroll>
      </section>

      <section
        className="py-24 lg:py-32 px-6 bg-gray-50 dark:bg-navy-900"
        data-testid="section-features"
      >
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <p className="text-emerald-500 text-sm font-semibold uppercase tracking-wider mb-4">
              How It Works
            </p>
            <h2
              className="text-3xl lg:text-4xl font-bold text-navy-900 dark:text-white"
              data-testid="text-features-title"
            >
              The easiest way to give your AI agent property data
            </h2>
          </div>
        </AnimateOnScroll>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <AnimateOnScroll key={f.title} animation="fade-up" delay={i * 100}>
              <div
                className="bg-white dark:bg-navy-800 rounded-2xl p-8 border border-gray-200 dark:border-navy-700 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`card-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <f.icon className="w-6 h-6 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold text-navy-900 dark:text-white mt-5">{f.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-3 leading-relaxed">
                  {f.description}
                </p>
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </section>

      <section
        className="py-24 lg:py-32 px-6 bg-[#0A0F1E]"
        data-testid="section-tools"
      >
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-emerald-500 text-sm font-semibold uppercase tracking-wider mb-4">
              Tool Catalog
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-white" data-testid="text-tools-title">
              11 tools built for real estate intelligence
            </h2>
            <p className="text-gray-400 mt-4">
              Every tool your AI agent needs to search, analyze, and enrich property data.
            </p>
          </div>
        </AnimateOnScroll>

        <div className="flex items-center justify-center gap-2 mt-10 flex-wrap">
          {(["All", "Light", "Heavy", "Premium"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setToolFilter(f)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 ${
                toolFilter === f
                  ? "bg-emerald-600 text-white"
                  : "bg-navy-800 text-gray-400 border border-navy-700 hover:border-gray-500"
              }`}
              data-testid={`button-filter-${f.toLowerCase()}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {filteredTools.map((tool, i) => (
            <AnimateOnScroll key={tool.name} animation="fade-up" delay={i * 50}>
              <div
                className="bg-navy-800 rounded-xl p-6 border border-navy-700 hover:border-emerald-500/40 hover:shadow-[inset_0_0_0_1px_rgba(16,185,129,0.2)] transition-all duration-200"
                data-testid={`card-tool-${tool.name}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="text-white font-mono font-semibold text-sm">{tool.name}</span>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full shrink-0 ${classificationConfig[tool.classification].badgeClass}`}>
                    {tool.classification}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mt-3 line-clamp-2">{tool.description}</p>
                <div className="mt-4 flex items-center gap-1">
                  <Coins className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400 text-sm font-medium">{tool.credits} credit{tool.credits !== 1 ? "s" : ""}</span>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </section>

      <section className="py-24 lg:py-32 px-6 bg-white dark:bg-navy-900" data-testid="section-code-demo">
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <p className="text-emerald-500 text-sm font-semibold uppercase tracking-wider mb-4">
              See It In Action
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 dark:text-white">
              From natural language to structured data
            </h2>
          </div>
        </AnimateOnScroll>

        <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-5 gap-8">
          <AnimateOnScroll animation="fade-up" delay={100} className="lg:col-span-3">
            <div className="flex items-center gap-2 flex-wrap">
              {(["Python", "TypeScript", "cURL"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setCodeTab(tab)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                    codeTab === tab
                      ? "bg-navy-900 text-white dark:bg-white dark:text-navy-900"
                      : "bg-gray-100 text-gray-600 dark:bg-navy-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-navy-700"
                  }`}
                  data-testid={`button-code-${tab.toLowerCase()}`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="bg-[#0A0F1E] rounded-xl p-6 mt-4 overflow-x-auto">
              <pre className="font-mono text-sm leading-relaxed">
                {codeTab === "Python" && (
                  <code>
                    <span className="text-purple-400">from</span><span className="text-gray-300"> mcp </span><span className="text-purple-400">import</span><span className="text-gray-300"> Client</span>{"\n"}
                    {"\n"}
                    <span className="text-gray-300">client = </span><span className="text-cyan-400">Client</span><span className="text-gray-300">(</span><span className="text-emerald-300">"https://propertymcp.co/mcp/sse"</span><span className="text-gray-300">)</span>{"\n"}
                    <span className="text-gray-300">client.</span><span className="text-cyan-400">authenticate</span><span className="text-gray-300">(</span><span className="text-emerald-300">"mcp_live_your_api_key"</span><span className="text-gray-300">)</span>{"\n"}
                    {"\n"}
                    <span className="text-gray-500"># Find vacant land in Texas over 5 acres</span>{"\n"}
                    <span className="text-gray-300">results = client.</span><span className="text-cyan-400">call</span><span className="text-gray-300">(</span><span className="text-emerald-300">"search_properties"</span><span className="text-gray-300">, {"{"}</span>{"\n"}
                    <span className="text-gray-300">    </span><span className="text-emerald-300">"state"</span><span className="text-gray-300">: </span><span className="text-emerald-300">"TX"</span><span className="text-gray-300">,</span>{"\n"}
                    <span className="text-gray-300">    </span><span className="text-emerald-300">"vacant"</span><span className="text-gray-300">: </span><span className="text-purple-400">True</span><span className="text-gray-300">,</span>{"\n"}
                    <span className="text-gray-300">    </span><span className="text-emerald-300">"acreage_min"</span><span className="text-gray-300">: </span><span className="text-amber-300">5</span><span className="text-gray-300">,</span>{"\n"}
                    <span className="text-gray-300">    </span><span className="text-emerald-300">"limit"</span><span className="text-gray-300">: </span><span className="text-amber-300">5</span>{"\n"}
                    <span className="text-gray-300">{"}"})</span>{"\n"}
                    {"\n"}
                    <span className="text-purple-400">for</span><span className="text-gray-300"> parcel </span><span className="text-purple-400">in</span><span className="text-gray-300"> results[</span><span className="text-emerald-300">"results"</span><span className="text-gray-300">]:</span>{"\n"}
                    <span className="text-gray-300">    owner = client.</span><span className="text-cyan-400">call</span><span className="text-gray-300">(</span><span className="text-emerald-300">"get_owner_info"</span><span className="text-gray-300">, {"{"}</span>{"\n"}
                    <span className="text-gray-300">        </span><span className="text-emerald-300">"parcel_id"</span><span className="text-gray-300">: parcel[</span><span className="text-emerald-300">"parcel_id"</span><span className="text-gray-300">]</span>{"\n"}
                    <span className="text-gray-300">    {"}"})</span>{"\n"}
                    <span className="text-gray-300">    </span><span className="text-cyan-400">print</span><span className="text-gray-300">(</span><span className="text-purple-400">f</span><span className="text-emerald-300">"{"{"}parcel['address']{"}"} — {"{"}owner['owner_name']{"}"}"</span><span className="text-gray-300">)</span>
                  </code>
                )}
                {codeTab === "TypeScript" && (
                  <code>
                    <span className="text-purple-400">import</span><span className="text-gray-300"> {"{"} </span><span className="text-cyan-400">McpClient</span><span className="text-gray-300"> {"}"} </span><span className="text-purple-400">from</span><span className="text-emerald-300"> "@modelcontextprotocol/sdk"</span><span className="text-gray-300">;</span>{"\n"}
                    {"\n"}
                    <span className="text-purple-400">const</span><span className="text-gray-300"> client = </span><span className="text-purple-400">new</span><span className="text-cyan-400"> McpClient</span><span className="text-gray-300">(</span><span className="text-emerald-300">"https://propertymcp.co/mcp/sse"</span><span className="text-gray-300">, {"{"}</span>{"\n"}
                    <span className="text-gray-300">  apiKey: </span><span className="text-emerald-300">"mcp_live_your_api_key"</span>{"\n"}
                    <span className="text-gray-300">{"}"});</span>{"\n"}
                    {"\n"}
                    <span className="text-purple-400">const</span><span className="text-gray-300"> results = </span><span className="text-purple-400">await</span><span className="text-gray-300"> client.</span><span className="text-cyan-400">callTool</span><span className="text-gray-300">(</span><span className="text-emerald-300">"search_properties"</span><span className="text-gray-300">, {"{"}</span>{"\n"}
                    <span className="text-gray-300">  state: </span><span className="text-emerald-300">"TX"</span><span className="text-gray-300">,</span>{"\n"}
                    <span className="text-gray-300">  vacant: </span><span className="text-purple-400">true</span><span className="text-gray-300">,</span>{"\n"}
                    <span className="text-gray-300">  acreage_min: </span><span className="text-amber-300">5</span><span className="text-gray-300">,</span>{"\n"}
                    <span className="text-gray-300">  limit: </span><span className="text-amber-300">5</span>{"\n"}
                    <span className="text-gray-300">{"}"});</span>{"\n"}
                    {"\n"}
                    <span className="text-purple-400">for</span><span className="text-gray-300"> (</span><span className="text-purple-400">const</span><span className="text-gray-300"> parcel </span><span className="text-purple-400">of</span><span className="text-gray-300"> results.results) {"{"}</span>{"\n"}
                    <span className="text-gray-300">  </span><span className="text-purple-400">const</span><span className="text-gray-300"> owner = </span><span className="text-purple-400">await</span><span className="text-gray-300"> client.</span><span className="text-cyan-400">callTool</span><span className="text-gray-300">(</span><span className="text-emerald-300">"get_owner_info"</span><span className="text-gray-300">, {"{"}</span>{"\n"}
                    <span className="text-gray-300">    parcel_id: parcel.parcel_id</span>{"\n"}
                    <span className="text-gray-300">  {"}"});</span>{"\n"}
                    <span className="text-gray-300">  console.</span><span className="text-cyan-400">log</span><span className="text-gray-300">(</span><span className="text-emerald-300">{"`"}${"{"}parcel.address{"}"} — ${"{"}owner.owner_name{"}"}{"`"}</span><span className="text-gray-300">);</span>{"\n"}
                    <span className="text-gray-300">{"}"}</span>
                  </code>
                )}
                {codeTab === "cURL" && (
                  <code>
                    <span className="text-gray-500"># Connect to MCP server and call search_properties</span>{"\n"}
                    <span className="text-cyan-400">curl</span><span className="text-gray-300"> -X POST </span><span className="text-emerald-300">https://propertymcp.co/mcp/messages</span><span className="text-gray-300"> \</span>{"\n"}
                    <span className="text-gray-300">  -H </span><span className="text-emerald-300">"Authorization: Bearer mcp_live_your_api_key"</span><span className="text-gray-300"> \</span>{"\n"}
                    <span className="text-gray-300">  -H </span><span className="text-emerald-300">"Content-Type: application/json"</span><span className="text-gray-300"> \</span>{"\n"}
                    <span className="text-gray-300">  -d </span><span className="text-emerald-300">'{"{"}</span>{"\n"}
                    <span className="text-emerald-300">    "tool": "search_properties",</span>{"\n"}
                    <span className="text-emerald-300">    "params": {"{"}</span>{"\n"}
                    <span className="text-emerald-300">      "state": "TX",</span>{"\n"}
                    <span className="text-emerald-300">      "vacant": true,</span>{"\n"}
                    <span className="text-emerald-300">      "acreage_min": 5,</span>{"\n"}
                    <span className="text-emerald-300">      "limit": 5</span>{"\n"}
                    <span className="text-emerald-300">    {"}"}</span>{"\n"}
                    <span className="text-emerald-300">  {"}"}'</span>
                  </code>
                )}
              </pre>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-up" delay={250} className="lg:col-span-2 mt-8 lg:mt-0">
            <span className="text-gray-500 text-sm font-mono">Response</span>
            <div className="bg-[#0A0F1E] rounded-xl p-6 mt-2">
              <pre className="font-mono text-sm leading-relaxed whitespace-pre-wrap break-words">
                <code>
                  <span className="text-gray-500">{"{"}</span>{"\n"}
                  <span className="text-cyan-400">  "results"</span><span className="text-gray-500">: [</span>{"\n"}
                  <span className="text-gray-500">    {"{"}</span>{"\n"}
                  <span className="text-cyan-400">      "parcel_id"</span><span className="text-gray-500">: </span><span className="text-emerald-300">"TX-342-88921"</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">      "address"</span><span className="text-gray-500">: </span><span className="text-emerald-300">"Rural Route 4, Marfa, TX 79843"</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">      "acreage"</span><span className="text-gray-500">: </span><span className="text-amber-300">12.5</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">      "assessed_value"</span><span className="text-gray-500">: </span><span className="text-amber-300">45000</span>{"\n"}
                  <span className="text-gray-500">    {"}"},{"\n"}</span>
                  <span className="text-gray-500">    {"{"}</span>{"\n"}
                  <span className="text-cyan-400">      "parcel_id"</span><span className="text-gray-500">: </span><span className="text-emerald-300">"TX-118-44203"</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">      "address"</span><span className="text-gray-500">: </span><span className="text-emerald-300">"County Rd 210, Alpine, TX 79831"</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">      "acreage"</span><span className="text-gray-500">: </span><span className="text-amber-300">8.2</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">      "assessed_value"</span><span className="text-gray-500">: </span><span className="text-amber-300">32000</span>{"\n"}
                  <span className="text-gray-500">    {"}"}</span>{"\n"}
                  <span className="text-gray-500">  ],</span>{"\n"}
                  <span className="text-cyan-400">  "count"</span><span className="text-gray-500">: </span><span className="text-amber-300">5</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">  "next_cursor"</span><span className="text-gray-500">: </span><span className="text-emerald-300">"eyJvZmZzZXQiOjV9"</span><span className="text-gray-500">,</span>{"\n"}
                  <span className="text-cyan-400">  "credits_used"</span><span className="text-gray-500">: </span><span className="text-amber-300">1</span>{"\n"}
                  <span className="text-gray-500">{"}"}</span>
                </code>
              </pre>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      <section
        className="py-24 lg:py-32 px-6 bg-navy-900"
        data-testid="section-workflow"
      >
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-emerald-500 text-sm font-semibold uppercase tracking-wider mb-4">
              Workflow Example
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              Watch your agent work in 3 steps
            </h2>
          </div>

          <div className="max-w-3xl mx-auto mt-6 text-center">
            <span className="text-gray-300 text-lg italic bg-navy-800 rounded-xl px-8 py-4 border border-navy-700 inline-block" data-testid="text-workflow-scenario">
              "Find 5 vacant parcels over 5 acres in Texas owned by out-of-state owners"
            </span>
          </div>
        </AnimateOnScroll>

        <div className="max-w-5xl mx-auto mt-16">
          <div className="flex flex-col lg:flex-row items-stretch gap-4 lg:gap-0">
            {workflowSteps.map((step, i) => (
              <AnimateOnScroll key={step.title} animation="fade-up" delay={i * 200} className="flex-1 flex items-stretch">
                <div className="flex items-stretch w-full">
                  <div
                    className={`bg-navy-800 rounded-2xl p-8 border flex flex-col w-full ${
                      step.isOutput
                        ? "border-navy-700 ring-1 ring-emerald-500/20 shadow-[0_0_40px_rgba(16,185,129,0.06)]"
                        : "border-navy-700"
                    }`}
                    data-testid={`card-workflow-${step.title.toLowerCase()}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center">
                      <step.icon className="w-5 h-5 text-emerald-500" />
                    </div>
                    {step.tools.length > 0 && (
                      <div className="flex flex-col gap-1.5 mt-4">
                        {step.tools.map((t) => (
                          <span key={t} className="font-mono text-sm bg-navy-700 rounded px-2 py-1 text-emerald-400 inline-block self-start">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-gray-400 text-sm mt-3 leading-relaxed flex-1">{step.description}</p>
                    {step.isOutput ? (
                      <p className="text-emerald-400 font-semibold text-sm mt-4">
                        Total: 21 credits &middot; ~2.1 seconds
                      </p>
                    ) : (
                      <span className="text-emerald-400 text-xs bg-emerald-500/10 rounded-full px-3 py-1 mt-4 inline-block self-start">
                        {step.creditLabel}
                      </span>
                    )}
                  </div>
                  {i < workflowSteps.length - 1 && (
                    <AnimateOnScroll animation="fade-up" delay={i * 200 + 150} className="hidden lg:flex items-center px-3 shrink-0">
                      <ChevronRight className="w-6 h-6 text-emerald-500" />
                    </AnimateOnScroll>
                  )}
                </div>
              </AnimateOnScroll>
            ))}
          </div>

        </div>
      </section>

      <section
        className="py-24 lg:py-32 px-6 bg-gray-50 dark:bg-navy-900"
        data-testid="section-integrations"
      >
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-navy-900 dark:text-white" data-testid="text-integrations-title">
              Works with your favorite AI tools
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-3">
              PropertyMCP is compatible with any MCP client.
            </p>
          </div>
        </AnimateOnScroll>

        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-12">
          {[
            { name: "OpenAI", initials: "O", gradient: "from-emerald-500 to-teal-500", desc: "Register as function tool" },
            { name: "Claude", initials: "C", gradient: "from-orange-500 to-amber-500", desc: "Add to MCP config" },
            { name: "Cursor", initials: "Cu", gradient: "from-gray-700 to-gray-900", desc: "Enable in settings" },
            { name: "LangChain", initials: "L", gradient: "from-blue-500 to-indigo-500", desc: "Import MCP client" },
            { name: "Replit", initials: "R", gradient: "from-blue-400 to-blue-600", desc: "Add via integrations" },
            { name: "Zapier", initials: "Z", gradient: "from-orange-400 to-orange-600", desc: "Connect via webhook" },
          ].map((platform, i) => (
            <AnimateOnScroll key={platform.name} animation="scale-up" delay={i * 80}>
              <div
                className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-navy-700 p-6 flex flex-col items-center text-center hover:shadow-md hover:border-emerald-500/30 transition-all duration-200"
                data-testid={`integration-${platform.name.toLowerCase()}`}
              >
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${platform.gradient} flex items-center justify-center`}>
                  <span className="text-white font-bold text-sm">{platform.initials}</span>
                </div>
                <span className="text-navy-900 dark:text-white font-semibold text-sm mt-3">{platform.name}</span>
                <span className="text-gray-500 dark:text-gray-400 text-xs mt-1">{platform.desc}</span>
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </section>

      <section className="py-24 lg:py-32 px-6 bg-[#0A0F1E]" data-testid="section-testimonials">
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              Trusted by developers building the future of real estate
            </h2>
          </div>

          <div className="mt-12 max-w-6xl mx-auto">
            <div
              ref={carouselRef}
              className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              onMouseEnter={() => { isPausedRef.current = true; }}
              onMouseLeave={() => { isPausedRef.current = false; }}
            >
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="min-w-[85%] sm:min-w-[45%] lg:min-w-[calc(33.333%-1rem)] snap-start shrink-0"
                >
                  <div
                    className="bg-navy-800 rounded-2xl p-8 border border-navy-700 flex flex-col h-full relative"
                    data-testid={`card-testimonial-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="absolute top-4 left-6 text-6xl text-emerald-500/10 font-serif select-none leading-none" aria-hidden="true">
                      &ldquo;
                    </span>
                    <p className="text-gray-300 text-base lg:text-lg leading-relaxed mt-4 relative z-10 flex-1">{t.quote}</p>
                    <div className="flex items-center gap-3 mt-6">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center shrink-0`}>
                        <span className="text-white font-bold text-sm">{t.initials}</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{t.name}</p>
                        <p className="text-gray-400 text-sm">{t.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 mt-8" data-testid="testimonial-dots">
              {testimonials.map((t, i) => (
                <button
                  key={t.name}
                  onClick={() => { scrollToTestimonial(i); setActiveTestimonial(i); }}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                    i === activeTestimonial ? "bg-emerald-500" : "bg-navy-700"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                  data-testid={`button-testimonial-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </AnimateOnScroll>
      </section>

      <section className="py-24 lg:py-32 px-6 bg-white" data-testid="section-pricing">
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-emerald-600 text-sm font-semibold uppercase tracking-wider mb-4">
              Pricing
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900" data-testid="text-pricing-title">
              Simple, transparent pricing
            </h2>
            <p className="text-gray-600 mt-3 text-lg">
              Start free. Scale as your agents grow.
            </p>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll animation="fade-up" delay={50}>
          <div
            className="max-w-4xl mx-auto mt-12 rounded-2xl border border-gray-200 p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
            data-testid="card-pricing-payg"
          >
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <h3 className="font-semibold text-lg text-navy-900" data-testid="text-payg-name">Pay-As-You-Go</h3>
                <p className="text-gray-500 text-sm">No commitment — buy credits when you need them</p>
              </div>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="text-center sm:text-right">
                <span className="text-3xl font-bold text-navy-900" data-testid="text-payg-price">$0.25</span>
                <span className="text-gray-500 text-sm ml-1">/ credit</span>
                <p className="text-xs text-[#6d727a] dark:text-gray-400">$10 min · valid 12 months</p>
              </div>
              <Button
                asChild
                size="default"
                className="font-semibold bg-navy-900 text-white border-navy-800"
                data-testid="button-pricing-payg"
              >
                <Link href={ctaHref}>
                  {prelaunch ? "Join Waitlist" : "Buy Credits"} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </AnimateOnScroll>

        <div className="max-w-6xl mx-auto mt-3 flex items-center gap-3">
          <div className="flex-1 border-t border-gray-200" />
          <span className="text-xs font-medium text-[#6d727a] dark:text-gray-400 uppercase tracking-wider">or save with a monthly plan</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mt-6">
          {[
            {
              name: "Starter",
              price: "$99",
              perCredit: "$0.099",
              credits: "1,000 credits/mo",
              detail: "~1,000 property searches",
              cta: "Start with Starter",
              featured: false,
            },
            {
              name: "Growth",
              price: "$299",
              perCredit: "$0.060",
              credits: "5,000 credits/mo",
              detail: "~5,000 property searches",
              cta: "Start with Growth",
              featured: false,
            },
            {
              name: "Scale",
              price: "$499",
              perCredit: "$0.033",
              credits: "15,000 credits/mo",
              detail: "~15,000 property searches",
              cta: "Start with Scale",
              featured: true,
            },
            {
              name: "Pro",
              price: "$999",
              perCredit: "$0.020",
              credits: "50,000 credits/mo",
              detail: "~50,000 property searches",
              cta: "Start with Pro",
              featured: false,
            },
          ].map((plan, i) => (
            <AnimateOnScroll key={plan.name} animation="fade-up" delay={i * 100}>
              <div
                className={`rounded-2xl p-8 text-center flex flex-col h-full ${
                  plan.featured
                    ? "bg-navy-900 ring-2 ring-emerald-500 relative scale-[1.02] pricing-float"
                    : "bg-white border border-gray-200"
                }`}
                data-testid={`card-pricing-${plan.name.toLowerCase()}`}
              >
                {plan.featured && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-semibold text-lg ${plan.featured ? "text-white" : "text-navy-900"}`}>
                  {plan.name}
                </h3>
                <div className="mt-4">
                  <span className={`text-5xl font-bold ${plan.featured ? "text-white" : "text-navy-900"}`}>
                    {plan.price}
                  </span>
                  <span className={`text-lg ${plan.featured ? "text-[#6d727a] dark:text-gray-400" : "text-gray-500"}`}>/mo</span>
                </div>
                <p className={`mt-4 ${plan.featured ? "text-gray-300" : "text-gray-600"}`}>
                  {plan.credits}
                </p>
                <p className={`text-sm mt-1 ${plan.featured ? "text-[#6d727a] dark:text-gray-400" : "text-gray-500"}`}>
                  {plan.detail}
                </p>
                <p className={`text-xs mt-2 font-medium ${plan.featured ? "text-emerald-400" : "text-emerald-600"}`} data-testid={`text-per-credit-${plan.name.toLowerCase()}`}>
                  {plan.perCredit} / credit
                </p>
                <div className="mt-8">
                  <Button
                    asChild
                    size="lg"
                    className={`w-full font-semibold ${
                      plan.featured
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-navy-900 text-white border-navy-800"
                    }`}
                    data-testid={`button-pricing-${plan.name.toLowerCase()}`}
                  >
                    <Link href={ctaHref}>
                      {prelaunch ? "Join Waitlist" : plan.cta} <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        <AnimateOnScroll animation="fade-up" delay={400}>
          <div className="text-center mt-8">
            <p className="text-gray-600">
              Need more? Enterprise plans start at $2,000/mo.{" "}
              <Link
                href="/pricing"
                className="text-emerald-600 hover:text-emerald-500 font-medium"
                data-testid="link-contact-sales"
              >
                Contact Sales <ArrowRight className="w-4 h-4 inline-block ml-0.5" />
              </Link>
            </p>
          </div>
          <div className="text-center mt-4">
            <p className="text-gray-500 text-sm">
              Start free — 25 credits, no credit card.{" "}
              <Link
                href="/pricing"
                className="text-emerald-600 font-medium"
                data-testid="link-full-pricing"
              >
                View full pricing details <ArrowRight className="w-3 h-3 inline-block ml-0.5" />
              </Link>
            </p>
          </div>
        </AnimateOnScroll>
      </section>

      <section className="py-24 lg:py-32 px-6 bg-[#0A0F1E] relative overflow-hidden" data-testid="section-final-cta">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[15%] left-[10%] w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-3xl animate-drift-1" />
          <div className="absolute top-[40%] right-[5%] w-[500px] h-[500px] rounded-full bg-cyan-400/[0.08] blur-3xl animate-drift-2" />
          <div className="absolute bottom-[10%] left-[30%] w-[500px] h-[500px] rounded-full bg-navy-700/[0.15] blur-3xl animate-drift-3" />
        </div>
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-2xl mx-auto text-center relative z-10">
            <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
              Start building with property data in minutes
            </h2>
            <p className="text-gray-400 text-lg mt-6">
              {prelaunch
                ? "We're launching soon. Join the waitlist and be first in line."
                : "25 free credits. No credit card required. Connect your AI agent today."}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link href={ctaHref} data-testid="link-final-cta-start">
                <span
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-8 py-4 font-semibold text-lg transition-all duration-200 cursor-pointer hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:scale-[1.02]"
                  data-testid="button-final-cta-start"
                >
                  {prelaunch ? "Join Waitlist" : "Create Free Account"}
                  <ArrowRight className="w-5 h-5" />
                </span>
              </Link>
              <Link href="/docs" data-testid="link-final-cta-docs">
                <span
                  className="inline-flex items-center justify-center text-white border border-navy-700 hover:border-emerald-500 hover:text-emerald-400 rounded-lg px-8 py-4 text-lg transition-all duration-200 cursor-pointer"
                  data-testid="button-final-cta-docs"
                >
                  Read Documentation
                </span>
              </Link>
            </div>
          </div>
        </AnimateOnScroll>
      </section>

      </main>

      <footer className="bg-[#0A0F1E] border-t border-navy-800 py-16" data-testid="section-footer">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <p className="text-white text-xl font-bold" data-testid="text-footer-brand">PropertyMCP</p>
              <p className="text-gray-400 text-sm mt-2">Property data for AI agents</p>
              <p className="text-gray-400 text-xs mt-6">&copy; 2026 PropertyMCP</p>
            </div>
            <div>
              <h3 className="text-gray-400 text-sm font-semibold uppercase tracking-wider mb-4">Product</h3>
              <ul className="space-y-3">
                <li><Link href="/docs#tools" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-tools">Tools Catalog</Link></li>
                <li><Link href="/pricing" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-pricing">Pricing</Link></li>
                <li><Link href="/docs" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-docs">Documentation</Link></li>
                <li><Link href="/dashboard/playground" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-playground">Playground</Link></li>
                <li><span className="text-gray-400 text-sm cursor-default" data-testid="link-footer-status">Status</span></li>
              </ul>
            </div>
            <div>
              <h3 className="text-gray-400 text-sm font-semibold uppercase tracking-wider mb-4">Company</h3>
              <ul className="space-y-3">
                <li><span className="text-gray-400 text-sm cursor-default" data-testid="link-footer-about">About</span></li>
                <li><span className="text-gray-400 text-sm cursor-default" data-testid="link-footer-blog">Blog</span></li>
                <li><a href="mailto:support@propertymcp.co" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-contact">Contact</a></li>
                <li><Link href="/privacy" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-privacy">Privacy Policy</Link></li>
                <li><Link href="/terms" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-terms">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-gray-400 text-sm font-semibold uppercase tracking-wider mb-4">Connect</h3>
              <ul className="space-y-3">
                <li><span className="text-gray-400 text-sm cursor-default" data-testid="link-footer-github">GitHub</span></li>
                <li><span className="text-gray-400 text-sm cursor-default" data-testid="link-footer-twitter">Twitter</span></li>
                <li><span className="text-gray-400 text-sm cursor-default" data-testid="link-footer-discord">Discord</span></li>
                <li><a href="mailto:support@propertymcp.co" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="link-footer-email">support@propertymcp.co</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-navy-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-gray-400 text-xs" data-testid="text-footer-built">Built on PropertyMCP</p>
            <p className="text-gray-400 text-xs" data-testid="text-footer-tagline">Made with data</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
